import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import WindowIcon from "@mui/icons-material/Window";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ScienceIcon from "@mui/icons-material/Science";
import { fetchWindowFormDefinitions } from "../api";
import { WINDOW_FORM_DEFAULT_DEFINITIONS } from "../windowFormDefinitionDefaults";
import { canAccessWindowForms } from "./windowFormsAccess";
import { selectUser } from "../../../features/userSlice";
import { selectRoles } from "../../../features/roleSlice";

const INTERIOR_DOOR_FORM_ID = 6;

const FormCard = ({ icon: Icon, title, subtitle, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="app-card group flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition hover:-translate-y-0.5 hover:border-sky-500/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
  >
    <span className="app-icon-tile h-10 w-10 rounded-xl bg-sky-500/15 text-sky-600 transition group-hover:bg-sky-500/25 dark:text-sky-300">
      <Icon sx={{ fontSize: 22 }} />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-semibold">{title}</span>
      <span className="app-muted block truncate text-xs">{subtitle}</span>
    </span>
    <ArrowForwardIcon
      className="app-muted shrink-0 transition group-hover:translate-x-0.5"
      sx={{ fontSize: 16 }}
    />
  </button>
);

const Group = ({ Icon, title, count, badge, children }) => (
  <section>
    <div className="mb-2.5 flex items-center gap-2">
      <Icon className="app-muted" sx={{ fontSize: 18 }} />
      <h3 className="text-xs font-semibold uppercase tracking-wider">{title}</h3>
      {badge ? (
        <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
          {badge}
        </span>
      ) : (
        typeof count === "number" && (
          <span className="app-chip app-muted tabular-nums">
            {count}
          </span>
        )
      )}
    </div>
    {children}
  </section>
);

/*
  Step 3 — pick the form.

  Previously this was a category tile that opened a dialog holding another
  image button. Every form the user can actually start is now one click, so
  there's nothing to discover behind a modal.
*/
const SelectFormStep = ({ onSelectForm }) => {
  const [definitions, setDefinitions] = useState(null); // null = still loading
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const user = useSelector(selectUser);
  const rolesState = useSelector(selectRoles);
  // Window forms are limited to the test group for now; everyone else gets the
  // "coming soon" panel. See windowFormsAccess.js.
  const windowFormsEnabled = canAccessWindowForms(
    user,
    rolesState?.roles || user?.roles || []
  );

  const builtInWindowFormIds = useMemo(
    () => new Set(Object.keys(WINDOW_FORM_DEFAULT_DEFINITIONS).map(Number)),
    []
  );

  useEffect(() => {
    if (!windowFormsEnabled) return undefined;

    let cancelled = false;
    setDefinitions(null);
    setLoadError("");

    fetchWindowFormDefinitions()
      .then((payload) => {
        if (cancelled) return;
        setDefinitions(
          (payload || []).filter(
            (definition) =>
              definition?.form_id &&
              !builtInWindowFormIds.has(definition.form_id)
          )
        );
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("Couldn't load the window forms.");
        setDefinitions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [builtInWindowFormIds, reloadKey, windowFormsEnabled]);

  const sectionCount = (definition) => {
    const count = definition?.sections?.length || 0;
    if (!count) return "Window quote request";
    return `${count} section${count === 1 ? "" : "s"}`;
  };

  return (
    <div className="space-y-6">
      <Group Icon={MeetingRoomIcon} title="Doors" count={1}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <FormCard
            icon={DoorFrontIcon}
            title="Interior Door"
            subtitle="Stock door packages · live pricing"
            onClick={() =>
              onSelectForm({
                target: "interior-door",
                formId: INTERIOR_DOOR_FORM_ID,
                formName: "Interior Door",
              })
            }
          />
        </div>
      </Group>

      <Group
        Icon={WindowIcon}
        title="Windows"
        count={
          windowFormsEnabled && definitions ? definitions.length : undefined
        }
        badge={windowFormsEnabled ? "Testing" : "Coming soon"}
      >
        {!windowFormsEnabled && (
          <div className="app-surface rounded-2xl border border-dashed p-6 text-center">
            <HourglassEmptyIcon className="app-muted" sx={{ fontSize: 26 }} />
            <p className="mt-1.5 text-sm font-semibold">
              Window forms are coming soon
            </p>
            <p className="app-muted mx-auto mt-1 max-w-sm text-xs leading-relaxed">
              We're still building these out. The Interior Door form above is
              ready to use.
            </p>
          </div>
        )}

        {windowFormsEnabled && (
          <p className="app-muted mb-2.5 flex items-center gap-1.5 text-xs">
            <ScienceIcon sx={{ fontSize: 14 }} />
            In testing — visible to you, hidden from everyone else for now.
          </p>
        )}

        {windowFormsEnabled && definitions === null && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                className="app-skeleton h-[66px] rounded-xl"
              />
            ))}
          </div>
        )}

        {definitions !== null && loadError && (
          <div className="app-surface rounded-xl border p-4 text-center">
            <p className="flex items-center justify-center gap-1.5 text-sm font-medium text-rose-600 dark:text-rose-300">
              <ErrorOutlineIcon sx={{ fontSize: 16 }} />
              {loadError}
            </p>
            <button
              type="button"
              onClick={() => setReloadKey((key) => key + 1)}
              className="app-btn app-btn-secondary app-btn-sm mt-2.5"
            >
              Try again
            </button>
          </div>
        )}

        {definitions !== null && !loadError && definitions.length === 0 && (
          <div className="app-surface rounded-xl border p-4 text-center">
            <p className="text-sm font-medium">No window forms are set up yet</p>
            <p className="app-muted mt-1 text-xs">
              Window Door staff can add them in Window Form Manager, under Windows &amp; Doors in the menu.
            </p>
          </div>
        )}

        {definitions !== null && !loadError && definitions.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {definitions.map((definition) => (
              <FormCard
                key={definition.form_id}
                icon={WindowIcon}
                title={
                  definition.form_name || `Custom Form ${definition.form_id}`
                }
                subtitle={sectionCount(definition)}
                onClick={() =>
                  onSelectForm({
                    target: "custom-window-form",
                    formId: definition.form_id,
                    formName: definition.form_name || "Custom Window Form",
                    definition,
                  })
                }
              />
            ))}
          </div>
        )}
      </Group>
    </div>
  );
};

export default SelectFormStep;
